import React, { useRef } from 'react';
import { QuestionType } from '../types';

interface QuizActionsProps {
  questionsCount: number;
  isSaving: boolean;
  saveError: string | null;
  onShowQuestionTypeModal: () => void;
  onSaveQuiz: () => void;
  showQuestionTypeModal: boolean;
  dropdownPosition: 'top' | 'bottom';
  dropdownRef: React.RefObject<HTMLDivElement>;
  onSelectQuestionType: (type: QuestionType) => void;
  onCloseQuestionTypeModal: () => void;
}

const QuizActions: React.FC<QuizActionsProps> = ({
  questionsCount,
  isSaving,
  saveError,
  onShowQuestionTypeModal,
  onSaveQuiz,
  showQuestionTypeModal,
  dropdownPosition,
  dropdownRef,
  onSelectQuestionType,
  onCloseQuestionTypeModal,
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-2 relative">
        <button 
          ref={buttonRef}
          onClick={onShowQuestionTypeModal}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700"
          type="button"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
          </svg>
          Add Question
        </button>
        
        {/* Question Type Dropdown */}
        {showQuestionTypeModal && (
          <div 
            className={`absolute ${dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'} left-0 bg-white rounded-lg shadow-lg border border-gray-200 w-80 z-50 animate-fadeIn`} 
            ref={dropdownRef}
            style={{
              animation: 'fadeIn 0.2s ease-in-out',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
            }}
          >
            {/* Arrow indicator */}
            <div 
              className={`absolute ${dropdownPosition === 'top' ? 'bottom-[-8px]' : 'top-[-8px]'} left-4 w-4 h-4 bg-white transform rotate-45`}
              style={{
                zIndex: 49,
                borderWidth: '1px',
                borderColor: '#e5e7eb',
                borderStyle: 'solid',
                borderRightWidth: dropdownPosition === 'top' ? '1px' : '0',
                borderBottomWidth: dropdownPosition === 'top' ? '1px' : '0',
                borderLeftWidth: dropdownPosition === 'top' ? '0' : '1px',
                borderTopWidth: dropdownPosition === 'top' ? '0' : '1px'
              }}
            ></div>
            <div className="p-2 relative z-50">
              <h3 className="text-sm font-medium mb-3 px-2 text-gray-700">Select Question Type</h3>
              
              <button
                onClick={() => onSelectQuestionType('multiple_choice')}
                className="w-full text-left px-3 py-3 hover:bg-blue-50 rounded-md flex items-start transition-colors"
                type="button"
              >
                <div className="text-blue-500 mr-3 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Multiple Choice</div>
                  <div className="text-xs text-gray-500">Select one correct answer from options</div>
                </div>
              </button>
              
              <button
                onClick={() => onSelectQuestionType('true_false')}
                className="w-full text-left px-3 py-3 hover:bg-blue-50 rounded-md flex items-start transition-colors"
                type="button"
              >
                <div className="text-green-500 mr-3 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-2-5a1 1 0 011-1h4a1 1 0 110 2H9a1 1 0 01-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">True / False</div>
                  <div className="text-xs text-gray-500">Simple true or false question</div>
                </div>
              </button>
              
              <button
                onClick={() => onSelectQuestionType('short_answer')}
                className="w-full text-left px-3 py-3 hover:bg-blue-50 rounded-md flex items-start transition-colors"
                type="button"
              >
                <div className="text-purple-500 mr-3 mt-0.5">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 13V5a2 2 0 00-2-2H4a2 2 0 00-2 2v8a2 2 0 002 2h3l3 3 3-3h3a2 2 0 002-2zM5 7a1 1 0 011-1h8a1 1 0 110 2H6a1 1 0 01-1-1zm1 3a1 1 0 100 2h3a1 1 0 100-2H6z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <div className="font-medium">Short Answer</div>
                  <div className="text-xs text-gray-500">Text response that matches a correct answer</div>
                </div>
              </button>
              
              <div className="mt-3 pt-2 border-t border-gray-100 flex justify-end px-2">
                <button
                  onClick={onCloseQuestionTypeModal}
                  className="px-3 py-1.5 text-xs text-gray-600 bg-gray-100 rounded hover:bg-gray-200 transition-colors flex items-center"
                  type="button"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {questionsCount > 0 && (
        <button
          onClick={onSaveQuiz}
          className="px-4 py-2 bg-green-600 text-white rounded-md text-sm hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          type="button"
          disabled={isSaving}
        >
          {isSaving ? 'Saving...' : 'Save Quiz'}
        </button>
      )}
      
      {saveError && (
        <div className="mt-2 text-red-500 text-sm">{saveError}</div>
      )}
    </div>
  );
};

export default QuizActions; 